const formatter = new Intl.RelativeTimeFormat('pt-BR', {
  numeric: 'auto'
})

type AvailableUnits = Extract<
  Intl.RelativeTimeFormatUnit,
  'year' | 'month' | 'week' | 'day' | 'hour' | 'minute' | 'second'
>

type Unit = {
  unit: AvailableUnits
  seconds: number
}

export const units: Unit[] = [
  { unit: 'week', seconds: 60 * 60 * 24 * 7 },
  { unit: 'day', seconds: 60 * 60 * 24 },
  { unit: 'hour', seconds: 60 * 60 },
  { unit: 'minute', seconds: 60 },
  { unit: 'second', seconds: 1 }
]

function calculateDiffInSeconds(date: Date, base: Date) {
  const diffInSeconds = Math.floor((date.getTime() - base.getTime()) / 1000)

  const isPast = diffInSeconds < 0
  const diffAbs = Math.abs(diffInSeconds)

  const prefix = isPast ? -1 : 1

  const diffInYears = base.getFullYear() - date.getFullYear()
  const diffInMonths = diffInYears * 12 + base.getMonth() - date.getMonth()

  return { diffAbs, prefix, diffInYears, diffInMonths }
}

/**
 * Converts a date to a relative time string in Brazilian Portuguese (e.g., "há 2 anos", "em 1 mês", "agora").
 *
 * @param date - The target date to compare.
 * @param base - The reference date (defaults to the current date/time).
 * @returns A relative time string describing how far `date` is from `base`.
 *
 * @example
 * ```ts
 * toRelativeTime(new Date(Date.now() - 60 * 1000)) // 'há 1 minuto'
 * toRelativeTime(new Date(Date.now() + 2 * 3600 * 1000)) // 'em 2 horas'
 * toRelativeTime(new Date(Date.now() - 365 * 24 * 3600 * 1000)) // 'ano passado'
 * ```
 */
export function toRelativeTime(date: Date, base: Date = new Date()): string {
  const { diffAbs, prefix, diffInMonths, diffInYears } = calculateDiffInSeconds(date, base)

  const isInMonthRange = diffAbs >= 60 * 60 * 24 * 30 && diffAbs < 60 * 60 * 24 * 365

  if (isInMonthRange) {
    return formatter.format(Math.abs(diffInMonths) * prefix, 'month')
  }

  const isOneYearOrMore = diffAbs >= 60 * 60 * 24 * 365

  if (isOneYearOrMore) {
    return formatter.format(Math.abs(diffInYears) * prefix, 'year')
  }

  for (const { unit, seconds } of units) {
    const diff = Math.floor(diffAbs / seconds)
    if (diff !== 0) {
      return formatter.format(diff * prefix, unit)
    }
  }

  return 'agora'
}
